import type { VPNClient } from '@meterup/proto/esm/api';
import type { CellProps, Column } from 'react-table';
import { Button, HStack, space } from '@meterup/atto';
import { expectDefinedOrThrow, ResourceNotFoundError } from '@meterup/common';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { fetchControllerJSON } from '../../api/controllers_api';
import { fetchVPNClients } from '../../api/vpn';
import { paths } from '../../constants';
import { useCurrentTimezone } from '../../providers/CurrentTimezoneProvider';
import { makeDrawerLink } from '../../utils/makeLink';
import { AutoTable } from '../AutoTable/AutoTable';
import { Nav } from '../Nav';
import { TimestampWithTimezone, TimezoneAbbreviation } from '../timestamps';

interface VPNClientsWidgetProps {
  controllerName: string;
}

function VPNClientsWidget({ controllerName }: VPNClientsWidgetProps) {
  const timezone = useCurrentTimezone();
  const tz = TimezoneAbbreviation(timezone);
  const drawerParams = Nav.useRegionParams('drawer', paths.drawers.VPNClientDetailPage);

  const controller = useQuery(
    ['controllers', controllerName],
    () => fetchControllerJSON(controllerName),
    { suspense: true },
  ).data;

  const vpnClients =
    useQuery(['controller', controllerName, 'vpn-clients'], () => fetchVPNClients(controllerName), {
      suspense: true,
    }).data ?? [];

  expectDefinedOrThrow(controller, new ResourceNotFoundError('Controller response not found'));
  expectDefinedOrThrow(
    vpnClients,
    new ResourceNotFoundError('VPN clients with user response not found'),
  );

  const columns: Column<VPNClient>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: (e) => e.name,
      },
      {
        Header: 'IP Address',
        accessor: (e) => e.ip_address,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (e: CellProps<VPNClient, string>) => (
          <div style={{ whiteSpace: 'normal' }}>{e.value}</div>
        ),
      },
      {
        Header: `Created At (${tz})`,
        accessor: (e) => e.created_at,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (e: CellProps<VPNClient, string>) => (
          <div>
            <TimestampWithTimezone value={e.value} timezone={timezone} />
          </div>
        ),
      },
      {
        Header: `User`,
        accessor: (e) => e.user_email,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (e: CellProps<VPNClient, string>) => (
          <div style={{ whiteSpace: 'normal' }}>{e.value}</div>
        ),
      },
    ],
    [timezone, tz],
  );

  return (
    <AutoTable
      isRowSelected={(row) => row.sid === drawerParams?.clientSid}
      linkProps={(row) => ({
        to: makeDrawerLink(paths.drawers.VPNClientDetailPage, {
          controllerName,
          clientSid: row.sid,
        }),
      })}
      tabs={
        <HStack spacing={space(8)}>
          <Button
            as={Link}
            to={makeDrawerLink(paths.drawers.VPNServerDetailPage, {
              controllerName,
            })}
            variant="secondary"
            arrangement="leading-icon"
          >
            Server details
          </Button>
        </HStack>
      }
      columns={columns}
      data={vpnClients}
      additionalControls={
        <Button
          as={Link}
          to={makeDrawerLink(paths.drawers.VPNClientCreate, {
            controllerName,
          })}
          variant="secondary"
          icon="plus-circle"
          arrangement="leading-icon"
        >
          Add client
        </Button>
      }
    />
  );
}

export default VPNClientsWidget;
